import { useEffect, useState } from 'react';
import { consultantApi } from '../../../utils/consultantApi';

// Voice provider selector — admin chooses which model answers customer voice
// calls first and which one the backend falls back to if the first fails.
// The two providers have different voices, so this is more than a backend
// detail — the customer can hear the difference.

const PROVIDERS = [
  // Provider names only; the model is picked in the AI tab (ModelSettingsPanel, «مکالمهٔ زنده»).
  { id: 'openai', label: 'OpenAI',        sub: 'مدل گفت‌وگوی صوتی شرکت OpenAI' },
  { id: 'gemini', label: 'Google Gemini', sub: 'مدل گفت‌وگوی صوتی شرکت Google' },
];

export default function VoiceProviderPanel() {
  const [primary, setPrimary] = useState(null);
  const [backup, setBackup]   = useState(null);
  const [initialPrimary, setInitialPrimary] = useState(null);
  const [initialBackup, setInitialBackup]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  // Load current settings on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await consultantApi.getVoiceProvider();
        if (!alive) return;
        setPrimary(data.primary); setBackup(data.backup);
        setInitialPrimary(data.primary); setInitialBackup(data.backup);
      } catch (e) {
        if (alive) setError(e.message || 'خواندن تنظیمات ناموفق بود.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const isDirty = (primary !== initialPrimary) || (backup !== initialBackup);
  const isInvalid = primary === backup;   // backup must differ from primary

  const save = async () => {
    if (!isDirty || isInvalid) return;
    setSaving(true); setError(null);
    try {
      await consultantApi.setVoiceProvider(primary, backup);
      setInitialPrimary(primary); setInitialBackup(backup);
      setSavedAt(Date.now());
    } catch (e) {
      setError(e.message || 'ذخیرهٔ تنظیمات ناموفق بود.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="voice-provider-panel"><p className="vp-loading">در حال بارگذاری…</p></div>;
  }

  return (
    <div className="voice-provider-panel">
      <header className="vp-header">
        <h2>مشاور صوتی</h2>
        <p className="vp-intro">
          انتخاب کنید کدام مدل به‌صورت پیش‌فرض با مشتری گفت‌وگو کند و کدام مدل
          درصورت خرابی مدل اول جایگزین شود.
        </p>
      </header>

      <RadioGroup
        legend="ارائه‌دهندهٔ اصلی"
        name="primary"
        value={primary}
        options={PROVIDERS}
        onChange={setPrimary}
      />

      <RadioGroup
        legend="ارائه‌دهندهٔ پشتیبان"
        name="backup"
        value={backup}
        options={PROVIDERS}
        onChange={setBackup}
        disabledIds={primary ? [primary] : []}
      />

      {isInvalid && (
        <div className="vp-warn" role="alert">
          ارائه‌دهندهٔ اصلی و پشتیبان نمی‌توانند یکی باشند.
        </div>
      )}

      {error && (
        <div className="vp-error" role="alert">{error}</div>
      )}

      <div className="vp-actions">
        <button
          type="button"
          className="vp-save-btn"
          onClick={save}
          disabled={!isDirty || isInvalid || saving}
        >
          {saving ? 'در حال ذخیره…' : 'ذخیره'}
        </button>
        {savedAt && !isDirty && (
          <span className="vp-saved-flag" role="status">ذخیره شد ✓</span>
        )}
      </div>
    </div>
  );
}

function RadioGroup({ legend, name, value, options, onChange, disabledIds = [] }) {
  return (
    <fieldset className="vp-group">
      <legend className="vp-legend">{legend}</legend>
      <div className="vp-options">
        {options.map((opt) => {
          const disabled = disabledIds.includes(opt.id);
          const checked  = value === opt.id;
          return (
            <label
              key={opt.id}
              className={`vp-option${checked ? ' is-checked' : ''}${disabled ? ' is-disabled' : ''}`}
            >
              <input
                type="radio"
                name={name}
                value={opt.id}
                checked={checked}
                disabled={disabled}
                onChange={() => onChange(opt.id)}
              />
              <span className="vp-option-text">
                <span className="vp-option-label">{opt.label}</span>
                <span className="vp-option-sub">{opt.sub}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
